import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  getAttendanceResponders as getAttendanceRespondersApi,
  stopProxyAttendance,
} from '../../../../../../db/classes';
import { attendanceWarningEvent } from '../../../../../../db/classes/pusher-events';
import { AttendanceWarningAction } from '../../../../../../db/classes/types';
import { usePusherEvent } from '../../../../../../pusher';
import { dateTimeDiff, format, unix } from '../../../../../../utils/datetime';
import { useInterval } from '../../../../../../utils/timer';
import { ATTENDANCE_TIMEOUT } from '../../../../constants';
import { useClassContext } from '../../../../Context';
import { useAttendancePageContext } from '../../Context';
import { addGetAttendanceRespondersDataToClass } from '../../helpers';
import { AttendancePageScreens } from '../../types';
import { useCourseContext } from '../../../../../course/Context';
import { useAppContext } from '../../../../../../app/Context';
import { toShortId } from '../../../../../../utils/helper';
import routes from '../../../../../../routes/router';
import { i18nNS } from '../../../../../../i18n';
import { useTranslation } from 'react-i18next';

const useAttendanceInProgress = () => {
  const { t } = useTranslation([i18nNS.COMMON]);

  const { session, setSlideTitleAndSubtitle } = useAppContext();
  const { course } = useCourseContext();
  const { cls, setClass } = useClassContext();
  const { setCurrentScreen } = useAttendancePageContext();

  const [remainingTime, setRemainingTime] = useState(0);

  const attendanceInProgress = cls?.attendanceInProgress;

  const remainingTimePercentage = useMemo(() => {
    if (remainingTime <= 0) return 0;
    const percentage = (100 * remainingTime) / ATTENDANCE_TIMEOUT;
    return percentage;
  }, [ATTENDANCE_TIMEOUT, remainingTime]);

  const markedStudentPercentage = useMemo(() => {
    if (!attendanceInProgress) return 0;
    const { nowMarkedPresent, enrolled } = attendanceInProgress;
    const percentage = (100 * nowMarkedPresent) / enrolled;
    return percentage;
  }, [attendanceInProgress?.nowMarkedPresent, attendanceInProgress?.enrolled]);

  const getAttendanceResponders = async () => {
    if (!course?.id) return;
    if (!cls?.id) return;

    const response = await getAttendanceRespondersApi({ classId: cls.id, courseId: course.id });

    if (!response) return;

    setClass((prevClass) => {
      return addGetAttendanceRespondersDataToClass(prevClass ?? undefined, response);
    });
  };

  const { start, stop } = useInterval(() => {
    if (!attendanceInProgress?.isInProgress) {
      onStop();
      return;
    }

    const diff = ATTENDANCE_TIMEOUT - dateTimeDiff(unix(), attendanceInProgress.startedAt, 'seconds');

    if (diff <= 0) {
      onStop();
      return;
    }

    if (diff % 30 === 0) {
      getAttendanceResponders();
    }

    setRemainingTime(diff);
  }, null);

  const stopAttendance = useCallback(async () => {
    if (!course?.id) return;
    if (!cls?.id) return;

    await stopProxyAttendance({
      courseId: course.id,
      classId: cls.id,
    });
  }, [course?.id, cls?.id]);

  const onStop = async () => {
    /**
     * not updating any state
     * 1. on manually stopping attendance
     * 2. on attendance time over
     * we receive pusher `web-attendancewarning`
     * whose callback will update the stats
     */
    stop();
    await stopAttendance();

    if (!session?.university.slug) return;
    if (!course?.id) return;
    if (!cls?.id) return;

    const baseUrl = process.env.REACT_APP_ACADLY_WEB_URL;

    const attendancePageRelativeWebUrl = routes.DO_NOT_USE_ACADLY_ATTENDANCE.acadlyWebUrl({
      universitySlug: session.university.slug,
      courseId: toShortId(course.id),
      classId: toShortId(cls.id),
    });

    const slideTitle = t('acadly_activity_plugin_course_code', {
      ns: i18nNS.COMMON,
      courseCode: course.code,
    });

    const slideSubtitle = t('attendance_was_recorded_in_lecture_scheduled_at_date_and_time_url', {
      ns: i18nNS.CLASS,
      dateAndTime: format(cls.attendanceInProgress?.startedAt ?? 0, "hh:mm a 'on' do MMM, yyyy"),
      url: `${baseUrl}${attendancePageRelativeWebUrl}`,
    });

    setSlideTitleAndSubtitle({ title: slideTitle, subtitle: slideSubtitle });
  };

  const handleOnClickStopAttendance = () => {
    return onStop();
  };

  useEffect(
    function startCountdown() {
      if (!attendanceInProgress?.isInProgress) return;
      start(1000);
    },
    [start, attendanceInProgress]
  );

  usePusherEvent(
    attendanceWarningEvent,
    useCallback(
      (event) => {
        const { action } = event.payload;
        if (action === AttendanceWarningAction.SHOW_WARNING) return;

        stop();

        setClass((prevClass) => {
          if (!prevClass) return null;
          return {
            ...prevClass,
            attendance: {
              ...prevClass.attendance,
              inProgress: false,
              numOfAttempts: prevClass.attendance.numOfAttempts + 1,
              markedInStudents:
                prevClass.attendance.markedInStudents +
                (prevClass.attendanceInProgress?.nowMarkedPresent ?? 0),
            },
            attendanceInProgress:
              prevClass.attendanceInProgress == null
                ? null
                : {
                    ...prevClass.attendanceInProgress,
                    isInProgress: false,
                  },
          };
        });
        setRemainingTime(0);
        setCurrentScreen(AttendancePageScreens.ATTENDANCE_RECORDED);
      },
      [setClass, stop]
    )
  );

  return {
    remainingTime,
    remainingTimePercentage,
    attendanceInProgress,
    markedStudentPercentage,
    handleOnClickStopAttendance,
  };
};

export default useAttendanceInProgress;
